// A registration code as a customer sees it: the code, a copy button, when it
// runs out, and (for a Scout) how to enter it. Shared by Register device and
// the device details modal so the two never drift apart.

import { format, formatDistanceToNowStrict } from 'date-fns';
import { ClipboardDocumentIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { copyRegistrationCode } from '../../lib/registrationCode';
import { ScoutCodeSteps, type ScoutStepsVariant } from './ScoutCodeSteps';

/** The code one character to a box, split in half so it reads like the Scout's field. */
function CodeSlots({ code, expired }: { code: string; expired: boolean }) {
  const half = Math.ceil(code.length / 2);
  return (
    <div className="flex justify-center gap-1 sm:gap-1.5" aria-hidden="true">
      {code.split('').map((ch, i) => (
        <span
          key={i}
          className={`flex h-10 w-[30px] items-center justify-center rounded-lg border font-mono text-lg font-bold sm:h-12 sm:w-10 sm:text-2xl ${
            i === half ? 'ml-2' : ''
          } ${expired ? 'border-slate-700 bg-slate-800/50 text-slate-500 line-through' : 'border-slate-600 bg-slate-800 text-white'}`}
        >
          {ch}
        </span>
      ))}
    </div>
  );
}

export function RegistrationCodePanel({
  code,
  expiresAt,
  steps,
}: {
  code: string;
  expiresAt: string | null;
  /** Which Scout steps to show; null for devices that don't take a typed code. */
  steps: ScoutStepsVariant | null;
}) {
  const expiry = expiresAt ? new Date(expiresAt) : null;
  const expired = !!expiry && expiry < new Date();
  return (
    <div className="space-y-4">
      <div
        className={`rounded-xl border px-3 py-5 text-center sm:px-5 ${
          expired ? 'border-red-500/30 bg-red-500/5' : 'border-slate-700 bg-slate-900/50'
        }`}
      >
        <p className="mb-3 text-sm font-medium text-slate-400">Registration code</p>
        {/* The slots are for the eye; this is the code for copy-and-paste and screen readers. */}
        <code className="sr-only">{code}</code>
        <CodeSlots code={code} expired={expired} />
        {expired ? (
          <p className="mx-auto mt-4 flex w-fit items-start gap-2 text-left text-sm font-medium text-red-400">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>This code has expired. Get a new code to register.</span>
          </p>
        ) : (
          <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
            <button
              type="button"
              onClick={() => copyRegistrationCode(code)}
              title="Copy registration code"
              aria-label="Copy registration code"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-1.5 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
            >
              <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
              Copy code
            </button>
            {expiry && (
              <p className="text-sm text-slate-400" title={expiry.toLocaleString('en-NZ')}>
                Expires in {formatDistanceToNowStrict(expiry)} · {format(expiry, 'd MMM, h:mmaaa')}
              </p>
            )}
          </div>
        )}
      </div>
      {!expired && steps && <ScoutCodeSteps variant={steps} />}
    </div>
  );
}
